import { BaseScreen, Device } from '@goodboydigital/astro';
import { Container } from 'pixi.js';

import { App, Shared } from '../App';
import { Game } from '../game/Game';
import { TouchControl } from '../ui/TouchControl';

export class ControlScreen extends BaseScreen
{
    public app: App;
    public game!: Game;
    public view!: Container;
    public accelerator!: TouchControl;
    public brake!: TouchControl;
    public rotatorCCW!: TouchControl;
    public rotatorCW!: TouchControl;

    constructor(app: App)
    {
        super(app);
        this.app = app;
        Shared.controls = this;
    }

    async init(): Promise<void>
    {
        if (this.view) return;
        this.view = new Container();
        this.game = Shared.game;

        this.accelerator = new TouchControl({
            game: this.game,
            base: Shared.assets.getTexture('acc-pedal-up'),
            baseDown: Shared.assets.getTexture('acc-pedal-down'),
            baseAnchorY: 1,
            baseDownAnchorX: 0.8,
            viewOffsetX: -0.2,
            viewOffsetY: 0.5,
            keys: ['ArrowUp', 'w'],
        });
        this.accelerator.signals.pressed.connect(() => this.game.race.checkBoost(true));
        this.accelerator.signals.released.connect(() => this.game.race.checkBoost(false));
        // this.accelerator.signals.pressed.connect(() => this.game.vehicle.playBoostEffect());
        this.view.addChild(this.accelerator.view);

        this.brake = new TouchControl({
            game: this.game,
            base: Shared.assets.getTexture('brake-pedal-up'),
            baseDown: Shared.assets.getTexture('brake-pedal-down'),
            baseAnchorY: 1,
            baseDownAnchorX: 0.8,
            viewOffsetX: 0.25,
            viewOffsetY: 0.5,
            keys: ['ArrowDown', 's'],
        });
        this.brake.signals.pressed.connect(() => this.game.race.checkBoost(false));
        this.brake.signals.released.connect(() => this.game.race.checkBoost(false));
        this.view.addChild(this.brake.view);

        this.rotatorCCW = new TouchControl({
            game: this.game,
            base: Shared.assets.getTexture('rotate-button'),
            icon: Shared.assets.getTexture('icon-replay'),
            iconScaleX: 1.25,
            iconScaleY: 1.25,
            viewOffsetX: 0.15,
            viewOffsetY: 0.2,
            keys: ['ArrowLeft', 'a'],
        });
        this.view.addChild(this.rotatorCCW.view);

        this.rotatorCW = new TouchControl({
            game: this.game,
            base: Shared.assets.getTexture('rotate-button'),
            icon: Shared.assets.getTexture('icon-replay'),
            iconScaleX: -1.25,
            iconScaleY: 1.25,
            viewOffsetX: -0.15,
            viewOffsetY: 0.2,
            keys: ['ArrowRight', 'd'],
        });
        this.view.addChild(this.rotatorCW.view);

        this.game.onPlay.connect(() => this.handleGameStart());
        this.game.race.onRaceStart.connect(() => this.handleGameStart());
        this.game.race.onRaceFinish.connect(() => this.handleGameFinish());
        this.game.onPause.connect(() => this.handleGamePause());
        this.game.onResume.connect(() => this.handleGameResume());

        this.view.visible = false;
    }

    private handleGameStart(): void
    {
        this.view.visible = true;

        // On race #0 (tutorial) controls are managed by the tutorial system
        // Auto-show only if it is race 1 or higher
        if (this.game.race.raceIndex > 0) this.showControls();
    }

    private handleGameFinish(): void
    {
        this.view.visible = false;
        this.hideControls();
    }

    private handleGamePause(): void
    {
        this.view.visible = false;
        if (this.game.race.raceIndex > 0) this.hideControls();
    }

    private handleGameResume(): void
    {
        this.view.visible = true;
        if (this.game.race.raceIndex > 0) this.showControls();
    }

    private resetPlayerAxis(): void
    {
        if (!this.game.player) return;
        this.game.player.axis.x = 0;
        this.game.player.axis.y = 0;
    }

    public showControls(): void
    {
        this.accelerator.show();
        this.brake.show();
        this.rotatorCCW.show();
        this.rotatorCW.show();
    }

    public hideControls(): void
    {
        this.resetPlayerAxis();
        this.accelerator.hide();
        this.brake.hide();
        this.rotatorCCW.hide();
        this.rotatorCW.hide();
    }

    public enableControls(): void
    {
        this.accelerator.enable();
        this.brake.enable();
        this.rotatorCCW.enable();
        this.rotatorCW.enable();
    }

    public disableControls(): void
    {
        this.accelerator.disable();
        this.brake.disable();
        this.rotatorCCW.disable();
        this.rotatorCW.disable();
    }

    public resize(): void
    {
        this.accelerator.resize(this.game.width * 0.245, this.game.height * 0.5);
        this.accelerator.view.x = this.game.width - (this.accelerator.width / 2);
        this.accelerator.view.y = this.game.height - (this.accelerator.view.height / 2);

        this.brake.resize(this.game.width * 0.245, this.game.height * 0.5);
        this.brake.view.x = this.accelerator.view.x - (this.accelerator.width / 2) - (this.brake.width / 2) - 1;
        this.brake.view.y = this.game.height - (this.brake.view.height / 2);

        this.rotatorCCW.resize(this.game.width * 0.245, this.game.height * 0.5);
        this.rotatorCCW.view.x = this.rotatorCCW.width / 2;
        this.rotatorCCW.view.y = this.game.height - (this.rotatorCCW.view.height / 2);

        this.rotatorCW.resize(this.game.width * 0.245, this.game.height * 0.5);
        this.rotatorCW.view.x = this.rotatorCCW.view.x + (this.rotatorCCW.width / 2) + (this.rotatorCW.width / 2) + 1;
        this.rotatorCW.view.y = this.game.height - (this.rotatorCCW.view.height / 2);
    }

    public update(): void
    {
        if (!this.game || !this.game.player) return;
        if (this.accelerator.pressing)
        {
            this.game.player.axis.x = 1;
        }
        else if (this.brake.pressing)
        {
            this.game.player.axis.x = -1;
        }
        else
        {
            this.game.player.axis.x = 0;
        }

        if (this.rotatorCCW.pressing)
        {
            this.game.player.axis.y = -1;
        }
        else if (this.rotatorCW.pressing)
        {
            this.game.player.axis.y = 1;
        }
        else
        {
            this.game.player.axis.y = 0;
        }
    }

    show(): void
    {
        this.view.visible = true;
        this.resize();
        if (this.game.playing) this.handleGameStart();
    }

    hidden(): void
    {
        if (Device.desktop) return;
        this.view.visible = false;
        this.accelerator.animateRelease();
        this.brake.animateRelease();
        this.rotatorCCW.animateRelease();
        this.rotatorCW.animateRelease();
    }
}
